import {
  CLIO_REPLICATION_REPORT_KINDS,
  CLIO_REPLICATION_SCHEDULER_ENTITIES,
  type ReplicateToDWConditionType,
  type ClioBackfillDefaultPeriods,
  type ClioFiscalPattern,
  type ClioReplicationReportReplicationConfig,
  type ClioReplicationSchedulerConfig,
  type ClioReplicationSchedulerEntity,
  type ClioReplicationSchedulerEntityConfig,
  type ClioReplicationSchedulerEntityMap,
  type ClioWeek53Handling,
  type ClioWeekStartDay,
} from '@xtnd/web/shared/types/tenantSettingTypes';

export interface ClioReplicationSchedulerEntityOption {
  key: ClioReplicationSchedulerEntity;
  label: string;
  description: string;
}

export const CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS: ClioReplicationSchedulerEntityOption[] = [
  { key: 'clio-matters', label: 'Clio Matters', description: 'Batch + on-demand matter replication.' },
  { key: 'clio-activities', label: 'Clio Activities', description: 'Batch + on-demand activity replication.' },
  { key: 'clio-wip', label: 'Clio WIP', description: 'WIP cache + activity reconciliation processing.' },
  { key: 'clio-bills', label: 'Clio Bills', description: 'Batch + on-demand bill replication.' },
  { key: 'clio-contacts', label: 'Clio Contacts', description: 'Batch + on-demand contact replication.' },
  { key: 'clio-users', label: 'Clio Users', description: 'User replication.' },
  { key: 'clio-bank-accounts', label: 'Clio Bank Accounts', description: 'Bank account replication.' },
  { key: 'clio-bank-transactions', label: 'Clio Bank Transactions', description: 'Bank transaction replication.' },
  { key: 'clio-line-items', label: 'Clio Line Items', description: 'Bill line item replication.' },
  { key: 'clio-allocations', label: 'Clio Allocations', description: 'Allocation replication.' },
  { key: 'clio-activity-descriptions', label: 'Clio Activity Descriptions', description: 'Activity description replication.' },
  { key: 'clio-expense-categories', label: 'Clio Expense Categories', description: 'Expense category replication.' },
  { key: 'clio-utbms-codes', label: 'Clio UTBMS Codes', description: 'UTBMS code replication.' },
  { key: 'clio-utbms-sets', label: 'Clio UTBMS Sets', description: 'UTBMS set replication.' },
  { key: 'clio-groups', label: 'Clio Groups', description: 'Group replication.' },
  { key: 'clio-credit-memos', label: 'Clio Credit Memos', description: 'Credit memo replication.' },
  { key: 'clio-trust-line-items', label: 'Clio Trust Line Items', description: 'Trust line item replication.' },
  { key: 'clio-activity-rates', label: 'Clio Activity Rates', description: 'Activity rate replication.' },
  { key: 'clio-custom-field-sets', label: 'Clio Custom Field Sets', description: 'Custom field set replication.' },
  { key: 'clio-custom-fields', label: 'Clio Custom Fields', description: 'Custom field replication.' },
  { key: 'clio-currencies', label: 'Clio Currencies', description: 'Currency replication.' },
  { key: 'clio-matter-stages', label: 'Clio Matter Stages', description: 'Matter stage replication.' },
  { key: 'juniper-eligible-matter-view', label: 'Juniper Eligible Matter View', description: 'Pre-aggregated Juniper eligible matter view rebuild.' },
];

const DEFAULT_ENTITY_CONFIG: ClioReplicationSchedulerEntityConfig = {
  replicateToClio: true,
  replicateToDW: false,
};

const DEFAULT_DW_CONDITION_TYPE: ReplicateToDWConditionType = 'rollingMonthsWindow';
const DEFAULT_DW_ROLLING_MONTHS = 12;

const DEFAULT_REPORT_REPLICATION_CONFIG: ClioReplicationReportReplicationConfig = {
  enabled: true,
  periodModel: 'CALENDAR',
  fiscalPattern: '4-4-5',
  weekStartDay: 'MONDAY',
  fiscalYearAnchorDate: '2024-01-01',
  week53Handling: 'P12',
  finalizeClosedPeriods: true,
  backfillDefaultPeriods: 12,
  reports: Object.fromEntries(
    CLIO_REPLICATION_REPORT_KINDS.map(reportKind => [reportKind, true])
  ) as ClioReplicationReportReplicationConfig['reports'],
  reportExportByMonth: Object.fromEntries(
    CLIO_REPLICATION_REPORT_KINDS.map(reportKind => [reportKind, true])
  ) as ClioReplicationReportReplicationConfig['reportExportByMonth'],
};

const DEFAULT_ENTITY_MAP = CLIO_REPLICATION_SCHEDULER_ENTITIES.reduce(
  (acc, entity) => {
    acc[entity] = { ...DEFAULT_ENTITY_CONFIG };
    return acc;
  },
  {} as ClioReplicationSchedulerEntityMap
);

export const DEFAULT_CLIO_REPLICATION_SCHEDULER_CONFIG: ClioReplicationSchedulerConfig = {
  enabled: true,
  entities: DEFAULT_ENTITY_MAP,
  reportReplication: DEFAULT_REPORT_REPLICATION_CONFIG,
  settingsComponent: 'DataReplicationConfigurationCard',
  healthCheckEndpoint: '/health/clio-replication-scheduler',
};

export function normalizeClioReplicationSchedulerConfig(
  rawConfig?: Partial<ClioReplicationSchedulerConfig> | null
): ClioReplicationSchedulerConfig {
  const entities = { ...DEFAULT_ENTITY_MAP };
  const incomingEntities = rawConfig?.entities || {};

  CLIO_REPLICATION_SCHEDULER_ENTITIES.forEach(entity => {
    const rawValue = incomingEntities[entity];
    if (typeof rawValue === 'boolean') {
      entities[entity] = { ...DEFAULT_ENTITY_CONFIG, replicateToClio: rawValue };
      return;
    }

    if (rawValue && typeof rawValue === 'object') {
      const value = rawValue as Partial<ClioReplicationSchedulerEntityConfig>;
      const { replicateToDWConditionType, replicateToDWValue } = normalizeDwWindowConfig(
        value.replicateToDWConditionType,
        value.replicateToDWValue
      );
      entities[entity] = {
        ...DEFAULT_ENTITY_CONFIG,
        ...value,
        replicateToClio: value.replicateToClio !== false,
        replicateToDW: value.replicateToDW === true,
        replicateToDWConditionType,
        replicateToDWValue,
      };
      return;
    }

    entities[entity] = { ...DEFAULT_ENTITY_CONFIG };
  });

  return {
    ...DEFAULT_CLIO_REPLICATION_SCHEDULER_CONFIG,
    ...rawConfig,
    enabled: rawConfig?.enabled !== false,
    entities,
    reportReplication: normalizeReportReplicationConfig(rawConfig?.reportReplication),
  };
}

function normalizeDwWindowConfig(
  conditionType: ClioReplicationSchedulerEntityConfig['replicateToDWConditionType'],
  rawValue: ClioReplicationSchedulerEntityConfig['replicateToDWValue']
): Pick<ClioReplicationSchedulerEntityConfig, 'replicateToDWConditionType' | 'replicateToDWValue'> {
  const normalizedConditionType: ReplicateToDWConditionType =
    conditionType === 'startDate' ? 'startDate' : DEFAULT_DW_CONDITION_TYPE;

  if (normalizedConditionType === 'rollingMonthsWindow') {
    const numericValue = toPositiveInt(rawValue);
    return {
      replicateToDWConditionType: 'rollingMonthsWindow',
      replicateToDWValue: numericValue ?? DEFAULT_DW_ROLLING_MONTHS,
    };
  }

  if (typeof rawValue === 'string' && isIsoDate(rawValue)) {
    return {
      replicateToDWConditionType: 'startDate',
      replicateToDWValue: rawValue,
    };
  }

  return {
    replicateToDWConditionType: 'startDate',
    replicateToDWValue: defaultStartDate(),
  };
}

function toPositiveInt(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : Number.NaN;

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return Math.floor(parsed);
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
}

function defaultStartDate(): string {
  const date = new Date();
  date.setMonth(date.getMonth() - DEFAULT_DW_ROLLING_MONTHS);
  return date.toISOString().slice(0, 10);
}

function normalizeReportReplicationConfig(
  rawConfig: Partial<ClioReplicationReportReplicationConfig> | undefined
): ClioReplicationReportReplicationConfig {
  const reports = {
    ...DEFAULT_REPORT_REPLICATION_CONFIG.reports,
  };

  for (const [reportKind, enabled] of Object.entries(rawConfig?.reports || {})) {
    reports[reportKind as keyof typeof reports] = enabled !== false;
  }

  const reportExportByMonth = {
    ...DEFAULT_REPORT_REPLICATION_CONFIG.reportExportByMonth,
  };

  for (const [reportKind, byMonth] of Object.entries(rawConfig?.reportExportByMonth || {})) {
    reportExportByMonth[reportKind as keyof typeof reportExportByMonth] = byMonth !== false;
  }

  return {
    ...DEFAULT_REPORT_REPLICATION_CONFIG,
    ...rawConfig,
    enabled: rawConfig?.enabled !== false,
    periodModel: rawConfig?.periodModel === 'FISCAL' ? 'FISCAL' : 'CALENDAR',
    fiscalPattern: toFiscalPattern(rawConfig?.fiscalPattern),
    weekStartDay: toWeekStartDay(rawConfig?.weekStartDay),
    fiscalYearAnchorDate:
      typeof rawConfig?.fiscalYearAnchorDate === 'string' &&
      /^\\d{4}-\\d{2}-\\d{2}$/.test(rawConfig.fiscalYearAnchorDate)
        ? rawConfig.fiscalYearAnchorDate
        : DEFAULT_REPORT_REPLICATION_CONFIG.fiscalYearAnchorDate,
    week53Handling: toWeek53Handling(rawConfig?.week53Handling),
    finalizeClosedPeriods: rawConfig?.finalizeClosedPeriods !== false,
    backfillDefaultPeriods: toBackfillDefaultPeriods(rawConfig?.backfillDefaultPeriods),
    reports,
    reportExportByMonth,
  };
}

function toFiscalPattern(value: string | undefined): ClioFiscalPattern {
  if (value === '4-5-4' || value === '5-4-4') {
    return value;
  }
  return '4-4-5';
}

function toWeekStartDay(value: string | undefined): ClioWeekStartDay {
  return value === 'SUNDAY' ? 'SUNDAY' : 'MONDAY';
}

function toWeek53Handling(value: string | undefined): ClioWeek53Handling {
  if (value === 'P11' || value === 'nearest-end') {
    return value;
  }
  return 'P12';
}

function toBackfillDefaultPeriods(value: number | undefined): ClioBackfillDefaultPeriods {
  if (value === 24 || value === 36) {
    return value;
  }
  return 12;
}
